/**
 * @fileoverview Compact modal footer button.
 * @description The small Cancel/Submit style used in modal footers. The shared Button atom's
 * "md"/"lg" sizes are both taller and full-width, which is right for auth cards but too large for a
 * modal footer row. Shows a spinner and disables itself while `isLoading`. A destructive
 * confirmation (e.g. Deactivate User) uses the "danger" variant.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import type { ReactNode } from 'react'

interface ModalFooterButtonProps {
  type: 'button' | 'submit'
  variant?: 'primary' | 'secondary' | 'danger'
  onClick?: () => void
  form?: string
  isLoading?: boolean
  disabled?: boolean
  'data-testid'?: string
  children: ReactNode
}

const VARIANT_CLASS_NAMES: Record<NonNullable<ModalFooterButtonProps['variant']>, string> = {
  primary: 'bg-primary text-white hover:opacity-90',
  secondary: 'border border-input-border bg-white text-ink hover:border-border-hover',
  danger: 'bg-danger text-white hover:opacity-90',
}

/**
 * @component
 * @param {ModalFooterButtonProps} props - Standard button type/variant plus a loading spinner.
 * @param {'button' | 'submit'} props.type - The button's type.
 * @param {'primary' | 'secondary' | 'danger'} [props.variant] - Filled primary, outlined
 * secondary, or filled danger for a destructive action.
 * @param {() => void} [props.onClick] - Click handler.
 * @param {string} [props.form] - The id of the form a submit button submits.
 * @param {boolean} [props.isLoading] - Shows a spinner and disables the button.
 * @param {boolean} [props.disabled] - Disables the button.
 * @param {ReactNode} props.children - The label.
 * @param {string} [props['data-testid']] - QA locator for the button.
 */
function ModalFooterButton({
  type,
  variant = 'secondary',
  onClick,
  form,
  isLoading,
  disabled,
  'data-testid': testId,
  children,
}: ModalFooterButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      form={form}
      data-testid={testId}
      disabled={isLoading || disabled}
      className={`flex cursor-pointer items-center gap-1.5 rounded-lg px-4 py-2 text-[13.5px] font-semibold transition-opacity disabled:cursor-not-allowed disabled:opacity-70 ${
        VARIANT_CLASS_NAMES[variant]
      }`}
    >
      {isLoading && (
        <span
          className={`h-3.5 w-3.5 animate-spin rounded-full border-2 ${
            variant === 'secondary'
              ? 'border-ink/20 border-t-ink'
              : 'border-white/40 border-t-white'
          }`}
          aria-hidden="true"
        />
      )}
      {children}
    </button>
  )
}

export default ModalFooterButton
